import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetStore } from '../../applicationSlice';
import { unauthorize } from '../../Security/authorizationSlice';
import { ToastMessageProps } from './';
import { ToastProgressProps } from './types';

interface ToastState {
	messages: ToastMessageProps[];
	progress: ToastProgressProps[];
}

const initialState: ToastState = {
	messages: [],
	progress: []
};

const toastSlice = createSlice({
	name: 'toastState',
	initialState,
	reducers: {
		/**
		 * Add an Toast Message to the collection to be displayed with a generated id.
		 */
		queueUncontrolledToast(state, action: PayloadAction<Omit<ToastMessageProps, 'id'>>) {
			const newAnnouncement = {
				...action.payload,
				id: Math.floor(Math.random() * 10000),
				type: 'message'
			} as ToastMessageProps;

			return {
				...state,
				messages: [...state.messages, { ...newAnnouncement }]
			};
		},
		/**
		 * Add a progress Toast to the queue with the given Id. If the id exists, the toast will be updated.
		 */
		queueControlledToast(state, action: PayloadAction<ToastMessageProps>) {
			// Deterimine if the toast exists
			const alreadyExists = state.messages.find((toast) => toast.id === action.payload.id);

			if (alreadyExists) {
				return {
					...state,
					messages: state.messages.map((toast) => (toast.id === action.payload.id ? { ...action.payload } : toast))
				};
			}

			return {
				...state,
				messages: [...state.messages, { ...action.payload }]
			};
		},
		/**
		 * Set the progress status of a specific Controlled toast
		 */
		setToastProgress(state, action: PayloadAction<ToastProgressProps>) {
			// If we don't have given toast id, do not add progress
			if (!state.messages.find((toast) => toast.id === action.payload.id)) {
				return state;
			}

			// Deterimine if the toast exists
			const alreadyExists = state.progress.find((toast) => toast.id === action.payload.id);

			if (alreadyExists) {
				return {
					...state,
					progress: state.progress.map((toast) => (toast.id === action.payload.id ? { ...action.payload } : toast))
				};
			}

			return {
				...state,
				progress: [...state.progress, { ...action.payload }]
			};
		},
		/**
		 * Remove a single Toast Message from the collection
		 */
		clearToast(state, action: PayloadAction<number>) {
			const copy = {
				messages: [...state.messages.filter((msg) => msg.id !== action.payload)],
				progress: [...state.progress.filter((msg) => msg.id !== action.payload)]
			};

			return {
				...state,
				...copy
			};
		},
		/**
		 * Remove all of the Toast Messages from the collection
		 */
		clearToaster() {
			return {
				...initialState
			};
		}
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			({ type }) => type === resetStore.type || type === unauthorize.type,
			() => initialState
		);
	}
});

export const { queueUncontrolledToast, queueControlledToast, setToastProgress, clearToast, clearToaster } = toastSlice.actions;

export default toastSlice;
